// Ponto único de leitura/escrita da sessão do usuário.
//
// O token e os dados do usuário ficam em `sessionStorage` (e não em
// `localStorage`) de propósito: assim a sessão morre quando a aba/janela é
// fechada ou o computador é desligado, e o login é pedido de novo.
// Cada aba tem seu próprio `sessionStorage`, então abrir o sistema em uma
// nova aba também exige novo login.

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

export interface StoredUser {
    user_id: number;
    name: string;
    profile: string;
    avatar?: string;
    company_id?: number;
}

/**
 * Lê o `exp` do JWT sem depender de biblioteca externa.
 * Retorna null quando o token não é um JWT legível.
 */
function getTokenExpiration(token: string): number | null {
    const payload = token.split('.')[1];
    if (!payload) return null;

    try {
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const exp = JSON.parse(json)?.exp;
        return typeof exp === 'number' ? exp : null;
    } catch {
        return null;
    }
}

/** Token vencido (ou ilegível) segundo o próprio `exp` do JWT. */
export function isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    const exp = getTokenExpiration(token);
    // Sem `exp` legível não dá para confiar: trata como expirado.
    if (exp === null) return true;
    return exp * 1000 <= Date.now();
}

export function getToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
    sessionStorage.setItem(TOKEN_KEY, token);
}

export function getStoredUser(): StoredUser | null {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;

    try {
        return JSON.parse(raw) as StoredUser;
    } catch {
        sessionStorage.removeItem(USER_KEY);
        return null;
    }
}

export function setStoredUser(user: StoredUser): void {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem('pendingPaymentChecked');
    sessionStorage.removeItem('pendingPaymentAdminAlert');
    // Restos de versões que guardavam a sessão em localStorage.
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
}

/**
 * Sessão válida = token presente e ainda dentro da validade.
 * Quando o token está vencido a sessão é limpa aqui mesmo, para a aplicação
 * nunca renderizar tela logada com credencial morta.
 */
export function hasValidSession(): boolean {
    const token = getToken();
    if (isTokenExpired(token)) {
        if (token) clearSession();
        return false;
    }
    return true;
}

/**
 * Descarta sessões antigas gravadas em `localStorage` por versões anteriores.
 * Sem isso, quem já estava logado continuaria entrando direto após reiniciar
 * o computador. Deve rodar uma vez, antes de qualquer leitura de sessão.
 */
export function purgeLegacyLocalStorageSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
}
